import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchPageBySlug } from '../lib/cms';

export default function Stiklinger() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      const data = await fetchPageBySlug('stiklinger');
      setPageData(data);
      setLoading(false);
    }
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-dahlia-500 mb-4" />
          <p className="text-forest-900 font-medium">Henter indhold...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20">
      <Helmet>
        <title>{pageData?.meta_titel?.da || pageData?.meta_title?.da || 'Dahlia knolde salg i Billund, Vejle & Kolding | Dahliahjørnet'}</title>
        <meta name="description" content={pageData?.meta_beskrivelse?.da || pageData?.meta_description?.da || 'Leder du efter Dahlia knolde salg i Billund, Vejle, Kolding eller Midtjylland?'} />
      </Helmet>
      {/* Header Banner */}
      <div className="relative h-[40vh] min-h-[300px] bg-forest-950 flex items-center justify-center text-center px-6">
        <img
          src={pageData?.banner_billede?.[0] || 'https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/c347dbd5-ff95-4119-bc58-7c327c410cd6/stikling-30.png'}
          alt={pageData?.titel?.da || 'Dahlia stiklinger'}
          className="absolute inset-0 w-full h-full object-cover object-center opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-950 via-forest-950/50 to-transparent" />
        <div className="relative z-10 max-w-4xl">
          <span className="text-white uppercase tracking-[0.25em] font-bold text-xs sm:text-sm mb-3 block">
            {pageData?.underoverskrift?.da || 'Dyrk dine egne dahliaer'}
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-white leading-tight">
            {pageData?.titel?.da || 'Vi har Dahlia-knolde til salg'}
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Text Content */}
          <div className="lg:col-span-7 flex flex-col gap-8">
            <div className="prose prose-forest max-w-none text-charcoal-700 leading-relaxed flex flex-col gap-6">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-forest-950">
                Dyrk dine egne dahliaer med knolde og stiklinger
              </h2>
              <p className="text-lg font-medium text-forest-950">
                Ønsker du at dyrke dahliaer i egen have? Hos os finder du dahlia-knolde til salg fra marts og frem, som i øvrigt kan forudbestilles hele året, så de er klar til dig, så snart sæsonen begynder. Vi forhandler desuden dahlia-stiklinger, hvis ikke du selv ønsker at sætte hele processen med at dyrke dine egne blomster i gang, men blot vil pryde din have med smukke blomster hurtigst muligt.
              </p>
              <p>
                Dahliahjørnet er beliggende i Vandel i Midtjylland, og vi ligger både tæt på Vejle, Billund og Kolding. Det er nemt at komme til os fra motorvejen, og du er altid velkommen til at tage et smut forbi os – vi har faktisk kunder fra hele Danmark, der kommer forbi for at plukke blomster og købe stiklinger.
              </p>

              <h2 className="text-2xl font-serif font-bold text-forest-950 mt-6">Har du spørgsmål til vores salg af dahlia-knolde?</h2>
              <p>
                Vores kunder er naturligvis altid velkomne til at kontakte os, hvis de vil høre mere om vores udvalg, priser eller andre forhold. Derudover er du altid velkommen til at komme forbi og se vores udvalg af dahlia-knolde til salg eller pluk-selv-blomster, hvor du kan vælge mellem et hav af former, størrelser og farver.
              </p>

              <h2 className="text-2xl font-serif font-bold text-forest-950 mt-6">Mere end 50.000 sorter</h2>
              <p>
                Dahlia-blomsten findes i et hav af sorter, størrelser og farver, og derfor har vi altid et bredt udvalg af dahlia-knolde til salg – hele sæsonen. Der findes over 50.000 forskellige sorter, hvoraf de største blomster er på størrelse med en tallerken og de mindste er på størrelse med en knytnæve. I øvrigt kaldes dahliaer af nogle for georginer.
              </p>
              <p>
                Alt efter hvilket udtryk, du ønsker, kan du derfor tilpasse din have med de sorter, der falder i din smag. Du er velkommen til at kontakte os, hvis du vil vide mere om hvilke sorter, vi sælger i denne sæson. Vi står altid klar til at vejlede om de forskellige egenskaber, der er tilknyttet hver enkelt sort.
              </p>
            </div>

            <div className="mt-8 p-6 bg-white border border-cream-200 rounded-3xl flex flex-col sm:flex-row items-center justify-between gap-6">
              <div>
                <h4 className="font-serif text-lg font-bold text-forest-950">Er du klar til at bestille dine knolde?</h4>
                <p className="text-sm text-charcoal-600 mt-1">Se vores store udvalg og bestil direkte online via vores bestillingsliste.</p>
              </div>
              <a
                href="/bestil-knolde"
                className="bg-dahlia-500 hover:bg-dahlia-600 text-white px-6 py-3 rounded-full font-semibold shadow-md hover:shadow-lg transition-all duration-200 whitespace-nowrap flex items-center gap-2"
              >
                <i className="fa-solid fa-cart-shopping" />
                Gå til bestilling
              </a>
            </div>
          </div>

          {/* Sidebar / Info */}
          <div className="lg:col-span-5 flex flex-col gap-8">
            {/* Highlights Box */}
            <div className="bg-white text-charcoal-800 p-8 rounded-3xl shadow-md border border-cream-200">
              <h3 className="text-xl font-serif font-bold text-forest-950 mb-6">Hvad kan du finde hos os?</h3>
              <ul className="flex flex-col gap-4 text-sm text-charcoal-700">
                <li className="flex items-start gap-3">
                  <span className="text-black mt-1.5 flex-shrink-0 text-[8px]">
                    <i className="fa-solid fa-circle" />
                  </span>
                  <span>Stort udvalg af pluk selv dahliablomster</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-black mt-1.5 flex-shrink-0 text-[8px]">
                    <i className="fa-solid fa-circle" />
                  </span>
                  <span>Et utal af farverige blomster i forskellige størrelser</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-black mt-1.5 flex-shrink-0 text-[8px]">
                    <i className="fa-solid fa-circle" />
                  </span>
                  <span>Køb knolde og stiklinger til din egen have</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
